import React, { useEffect, useMemo, useState } from 'react'
import type { ChangeEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Option, SelectList } from '@indeed/ifl-components'

import {
  fetchEmployerVenueName,
  searchEmployerVenues,
} from 'syft-acp-core/store/employer-venue-search/actions'
import {
  selectEmployerVenueSearch,
  selectVenueName,
} from 'syft-acp-core/store/employer-venue-search/selectors'
import { setFilter } from 'syft-acp-core/store/filters/actions'

import { venueSearchView } from './EmployerAccountsList.helpers'
import type { EmployerAccountsVenueFilterProps } from './EmployerAccountsList.types'

export const VENUE_FILTER_NAME = 'venue_id'

const ALL_VENUES = 'all-venues'

export const SEARCH_DELAY_MS = 300

// The popup is portaled to <body>, but ACP's page body (`.App .content`) is its own stacking context
// at z-index 5, so IFL's default popup z-index (1) renders underneath the accounts table. 1000 is the
// layer Bootstrap dropdowns use on the same page: above page content, below the fixed nav and modals.
export const POPUP_Z_INDEX = 1000

// Sized to sit alongside the legacy Bootstrap Name and E-mail inputs (34px high, 14px text).
const compactText = { fontSize: 2 }

/**
 * Venue filter for the employer Accounts tab. Selecting a venue sets `venue_id` in the
 * URL query; choosing "All venues" removes it.
 *
 * Venues are searched on the server one page at a time while the popup is open, so
 * opening the tab never downloads an employer's full venue list.
 */
export const EmployerAccountsVenueFilter = ({ employerID }: EmployerAccountsVenueFilterProps) => {
  const dispatch = useDispatch()
  const venueSearch = useSelector(selectEmployerVenueSearch)
  const value = useSelector(
    state => state.routing?.locationBeforeTransitions?.query?.[VENUE_FILTER_NAME] as string | undefined,
  )
  const selectedName = useSelector(state => selectVenueName(state, value))
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const query = search.trim()

  useEffect(() => {
    if (!isOpen || !employerID) return undefined

    const timer = setTimeout(
      () => dispatch(searchEmployerVenues({ employerID, query })),
      query ? SEARCH_DELAY_MS : 0,
    )

    return () => clearTimeout(timer)
  }, [dispatch, employerID, isOpen, query])

  useEffect(() => {
    if (employerID && value && !selectedName) dispatch(fetchEmployerVenueName({ employerID, venueID: value }))
  }, [dispatch, employerID, value, selectedName])

  const { venues, isSearching, failed, noMatches, hiddenMatches } = venueSearchView(
    venueSearch,
    employerID,
    query,
  )
  const options = useMemo(
    () => venues.map(venue => ({ id: venue.id, label: `${venue.id}: ${venue.name}` })),
    [venues],
  )

  const selectedLabel = (selectedValues: string[]) =>
    selectedName ? `${selectedValues[0]}: ${selectedName}` : `Venue ${selectedValues[0]}`

  const handleSelectionChange = ([selected]: string[]) =>
    dispatch(setFilter(VENUE_FILTER_NAME, selected && selected !== ALL_VENUES ? selected : null))

  return (
    <Box sx={{ inlineSize: '340px' }}>
      <SelectList
        aria-label="Filter accounts by venue"
        placeholder="All venues"
        size="sm"
        sx={{ ...compactText, borderRadius: 'sm' }}
        filterable
        portaledPopup
        popupProps={{ sx: { zIndex: POPUP_Z_INDEX, inlineSize: '30rem', maxInlineSize: '90vw' } }}
        filterProps={{
          'aria-label': 'Search venues by name or ID',
          placeholder: 'Search by name or ID',
          value: search,
          onChange: (event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value),
        }}
        selectedValues={value ? [value] : []}
        setSelectionDisplayLabel={selectedLabel}
        onSelectionChange={handleSelectionChange}
        onOpenChange={open => {
          setIsOpen(open)
          if (!open) setSearch('')
        }}
      >
        {!search && (
          <Option value={ALL_VENUES} sx={compactText}>
            All venues
          </Option>
        )}
        {isSearching && (
          <Option value="searching" disabled sx={compactText}>
            Searching…
          </Option>
        )}
        {failed && (
          <Option value="search-failed" disabled sx={compactText}>
            Couldn&apos;t load venues. Close the list and open it again to retry.
          </Option>
        )}
        {options.map(option => (
          <Option key={option.id} value={String(option.id)} sx={compactText}>
            {option.label}
          </Option>
        ))}
        {noMatches && (
          <Option value="no-matches" disabled sx={compactText}>
            No matches.
          </Option>
        )}
        {hiddenMatches > 0 && (
          <Option value="more-matches" disabled sx={compactText}>
            {`${hiddenMatches.toLocaleString('en-GB')} more. Keep typing to narrow the list.`}
          </Option>
        )}
      </SelectList>
    </Box>
  )
}
